import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import useModal, { UseModal } from 'hooks/useModal';
import { ApplicationError } from 'types/errorHandling';

import { FileFieldOnChange, FileFieldType } from '../../molecules/form/typing';
import { GET_DEFAULT_FILE_READ_WRITE_STATE } from '../fileViewModal/config';
import { FileInformation, FileReadWriteState } from '../fileViewModal/types';
import { TEXTS } from './config';
import { Accept, AddFileMediator, DeleteFileHandler } from './types';

const useFile = (
  value: FileFieldType,
  onChange: FileFieldOnChange,
  fileDisplayStr: string,
  accept?: Accept
) => {
  // region *******************************DATA*********************************
  const handleAppError = useApplicationErrorHandling();

  const [showFileModal, toggleShowFileModal]: UseModal = useModal();

  const fileRef = useRef<Nullable<HTMLInputElement>>(null);

  const [fileViewerInfo, setFileViewerInfo] = useState<
    FileInformation[] | undefined
  >();

  // endregion

  // region *****************************CALLBACKS******************************
  const addFileHandler = useCallback(
    (files: Nullable<FileList>) => {
      if (files) {
        // sometimes type doesn't find the file ext, so must find it manually
        const fileExtension =
          files[0].type ||
          files[0].name.substring(files[0].name.lastIndexOf('.'));

        // If there is no extension, or the extension doesn't match the accepted ones, then display an error
        if (
          !fileExtension ||
          fileExtension === '' ||
          (accept && !accept.includes(fileExtension.toLowerCase()))
        ) {
          handleAppError(
            new ApplicationError(
              TEXTS.INVALID_FILE_TYPE,
              TEXTS.INVALID_FILE_TYPE,
              {
                details: { file: files[0] },
              }
            )
          );
          return;
        }

        if (onChange) {
          setFileViewerInfo([
            {
              fileUrl: files[0],
              id: 'file',
              readWriteState: GET_DEFAULT_FILE_READ_WRITE_STATE(),
            },
          ]);
          onChange(files[0]);
        }
      }
    },
    [accept, handleAppError, onChange]
  );

  const addFileMediator: AddFileMediator = useCallback(
    (ev) => {
      if (ev.dataTransfer) {
        addFileHandler(ev.dataTransfer.files);
      } else {
        addFileHandler((ev.target as HTMLInputElement).files);
      }
    },
    [addFileHandler]
  );

  const deleteFileHandler: DeleteFileHandler = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // without preventDefault(), a file explorer appears when removing the file
      e.preventDefault();
      setFileViewerInfo(undefined);
      if (onChange) {
        onChange('');
      }
    },
    [onChange]
  );

  const setFileViewerVisible = useCallback(
    (visible: boolean) => {
      if (!visible) {
        setFileViewerInfo(undefined);
        return;
      }

      if (!value) return;

      setFileViewerInfo([
        { fileUrl: value, id: 'file', readWriteState: FileReadWriteState.Edit },
      ]);
    },
    [value]
  );

  const handleShowFileModal = useCallback(
    (visible: boolean) => {
      toggleShowFileModal();
      setFileViewerVisible(visible);
    },
    [setFileViewerVisible, toggleShowFileModal]
  );
  // endregion

  return useMemo(
    () => ({
      value,
      fileRef,
      showFileModal,
      fileName: fileDisplayStr,
      deleteFileHandler,
      addFileMediator,
      fileViewerInfo,
      setFileViewerVisible,
      handleShowFileModal,
    }),
    [
      value,
      showFileModal,
      fileDisplayStr,
      deleteFileHandler,
      addFileMediator,
      fileViewerInfo,
      setFileViewerVisible,
      handleShowFileModal,
    ]
  );
};

export default useFile;
